import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { ArrowRight, Bot, User, Send, Volume2, Loader2, Trash2 } from "lucide-react";
import { Helmet } from "react-helmet";
import { AACKeyboard } from "@/components/aac/AACKeyboard";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { speak, stopSpeaking } from "@/lib/ttsService";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { supabase } from "@/integrations/supabase/client";

interface Message {
  role: "user" | "assistant";
  content: string;
}

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-chat`;

export default function AIChat() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const initialMessageSent = useRef(false);

  const clearChat = () => {
    setMessages([]);
    toast.success(t("chatCleared"));
  };

  // Check for initial message from sessionStorage (e.g., from AAC keyboard)
  useEffect(() => {
    const initialMessage = sessionStorage.getItem("aiChatInitialMessage");
    if (initialMessage && !initialMessageSent.current) {
      sessionStorage.removeItem("aiChatInitialMessage");
      setInput(initialMessage);
      initialMessageSent.current = true;
    }
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSpeak = (text: string) => {
    if (!text.trim()) return;
    speak({ text });
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = { role: "user", content: input.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);

    let assistantContent = "";

    try {
      // Get the user's session token for proper authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Authentication required");
      }

      const response = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ messages: newMessages }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t("sendMessageError"));
      }

      if (!response.body) throw new Error("No response body");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              assistantContent += content;
              setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last?.role === "assistant") {
                  return prev.map((m, i) =>
                    i === prev.length - 1 ? { ...m, content: assistantContent } : m
                  );
                }
                return [...prev, { role: "assistant", content: assistantContent }];
              });
            }
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }
    } catch (error) {
      console.error("Chat error:", error);
      toast.error(error instanceof Error ? error.message : t("sendMessageError"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t("aiChat")} - My EyeControl</title>
        <meta name="description" content="צ'אט עם בינה מלאכותית בעברית" />
      </Helmet>

      <MainLayout>
        <div className="flex-1 flex flex-col overflow-hidden" dir="rtl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-card/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-xl font-bold text-foreground">{t("aiChat")}</h1>
            </div>
            <div className="flex items-center gap-2">
              {messages.length > 0 && (
                <Button variant="ghost" size="sm" onClick={clearChat} data-scan-item>
                  <Trash2 className="w-4 h-4 ml-1" />
                  {t("clear")}
                </Button>
              )}
              <Button
                variant="ghost"
                onClick={() => navigate("/")}
                className={cn("flex items-center gap-2")}
              >
                <ArrowRight className="w-5 h-5" />
                <span>{t("back")}</span>
              </Button>
            </div>
          </div>

          {/* Chat Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <ScrollArea className="flex-1 p-4" ref={scrollRef}>
              <div className="max-w-2xl mx-auto space-y-4">
                {messages.length === 0 && (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gradient-to-br from-violet-500/20 to-purple-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Bot className="w-8 h-8 text-violet-500" />
                    </div>
                    <h2 className="text-lg font-semibold text-foreground mb-2">
                      {t("helloImHere")}
                    </h2>
                    <p className="text-muted-foreground">
                      {t("writeMessageBelow")}
                    </p>
                  </div>
                )}

                <AnimatePresence>
                  {messages.map((message, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex gap-3 ${
                          message.role === "user" ? "flex-row-reverse" : ""
                        }`}
                      >
                        <div
                          className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                            message.role === "user"
                              ? "bg-primary"
                              : "bg-gradient-to-br from-violet-500 to-purple-600"
                          }`}
                        >
                          {message.role === "user" ? (
                            <User className="w-5 h-5 text-primary-foreground" />
                          ) : (
                            <Bot className="w-5 h-5 text-white" />
                          )}
                        </div>
                        <div
                          className={`flex-1 max-w-[80%] ${
                            message.role === "user" ? "text-left" : "text-right"
                          }`}
                        >
                          <div
                            className={`inline-block p-3 rounded-2xl ${
                              message.role === "user"
                                ? "bg-primary text-primary-foreground rounded-br-sm"
                                : "bg-muted text-foreground rounded-bl-sm"
                            }`}
                          >
                            <p className="whitespace-pre-wrap">{message.content}</p>
                          </div>
                          {message.role === "assistant" && message.content && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSpeak(message.content)}
                              data-scan-item
                              className="mt-1"
                            >
                              <Volume2 className="w-4 h-4 ml-1" />
                              {t("readAloud")}
                            </Button>
                          )}
                        </div>
                      </motion.div>
                    ))}
                </AnimatePresence>

                {isLoading && messages[messages.length - 1]?.role === "user" && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex gap-3"
                  >
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                    <div className="bg-muted rounded-2xl rounded-bl-sm p-3">
                      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                    </div>
                  </motion.div>
                )}
              </div>
            </ScrollArea>

            {/* Keyboard */}
            <div className="border-t border-border bg-background p-4">
              <div className="max-w-2xl mx-auto">
                <AACKeyboard
                  value={input}
                  onChange={setInput}
                  onDone={sendMessage}
                />
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
    </>
  );
}
